import React, { useRef, useEffect } from 'react';
import { Card, CardContent } from '@/shared/ui/card';
import { Label } from '@/shared/ui/label';
import { Camera, Upload, Loader2 } from 'lucide-react';

interface ImageCaptureStepProps {
  captureMethod: 'snap_card' | 'upload_image' | 'bulk_upload';
  onImages: (files: File[]) => void;
  isExtracting?: boolean;
}

export function ImageCaptureStep({
  captureMethod,
  onImages,
  isExtracting = false,
}: ImageCaptureStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isMobileOrTablet, setIsMobileOrTablet] = React.useState(false);

  useEffect(() => {
    const check = () => {
      const ua = navigator.userAgent.toLowerCase();
      const mobile = /iphone|ipod|android.*mobile|windows phone/i.test(ua);
      const tablet = /ipad|android(?!.*mobile)|tablet/i.test(ua);
      const hasCamera =
        'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices;
      setIsMobileOrTablet((mobile || tablet || navigator.maxTouchPoints > 0) && !!hasCamera);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const multiple = captureMethod === 'bulk_upload';
  const accept = 'image/*';

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length > 0) onImages(files);
    e.target.value = '';
  };

  const triggerUpload = () => fileInputRef.current?.click();
  const triggerCamera = () => cameraInputRef.current?.click();

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleFileChange}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept={accept}
        capture="environment"
        className="hidden"
        onChange={handleFileChange}
      />

      <Label className="text-base font-medium">
        {captureMethod === 'bulk_upload'
          ? 'Select multiple business card images'
          : 'Capture or choose a business card image'}
      </Label>

      {isExtracting && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Extracting contact info...</span>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {isMobileOrTablet && (captureMethod === 'snap_card' || captureMethod === 'upload_image') && (
          <Card
            className="cursor-pointer transition-colors hover:border-primary"
            onClick={triggerCamera}
          >
            <CardContent className="flex flex-col items-center gap-2 p-6">
              <Camera className="h-12 w-12 text-primary" />
              <span className="font-medium">Snap Card</span>
              <span className="text-sm text-muted-foreground">Camera capture</span>
            </CardContent>
          </Card>
        )}
        <Card
          className="cursor-pointer transition-colors hover:border-primary"
          onClick={triggerUpload}
        >
          <CardContent className="flex flex-col items-center gap-2 p-6">
            <Upload className="h-12 w-12 text-primary" />
            <span className="font-medium">
              {captureMethod === 'bulk_upload' ? 'Select multiple cards' : 'Upload image'}
            </span>
            <span className="text-sm text-muted-foreground">
              {captureMethod === 'bulk_upload'
                ? 'Photo or screenshot for each card'
                : 'Photo or screenshot'}
            </span>
          </CardContent>
        </Card>
      </div>

      {captureMethod === 'bulk_upload' && (
        <p className="text-sm text-muted-foreground">
          You can select several images at once. We’ll process them one by one.
        </p>
      )}
    </div>
  );
}
